export type GateCheck = 'pass' | 'warn' | 'fail';
export type InjectGateDecision = 'inject' | 'reject' | 'hold';

export type InjectGateResult = {
  brand: GateCheck;
  product: GateCheck;
  infringement: GateCheck;
  invalid: boolean;
  decision: InjectGateDecision;
  summary: string;
  reasons: string[];
  selected_product?: string | null;
  product_corrected?: boolean;
  brand_ip_merchandise?: boolean;
  article_category?: string | null;
};

/** Auto-process and manual inject can send this without a catalog product. */
export function isBrandIpMerchandiseInject(
  gate: Pick<InjectGateResult, 'brand_ip_merchandise' | 'decision'> | null | undefined
): boolean {
  return Boolean(gate?.brand_ip_merchandise && gate.decision === 'inject');
}

/** Skip the Store-catalog wait when Auto-process starts on the listing already on screen. */
export function shouldWaitForStoreCatalog(opts: {
  currentGroupKey: string | null;
  targetGroupKey: string;
  catalogSettled: boolean;
  storeLoading: boolean;
}): boolean {
  if (opts.currentGroupKey === opts.targetGroupKey && opts.catalogSettled && !opts.storeLoading) {
    return false;
  }
  return true;
}

export function waitUntil(
  predicate: () => boolean,
  { timeoutMs = 20000, intervalMs = 100 }: { timeoutMs?: number; intervalMs?: number } = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (predicate()) {
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        reject(new Error('Timed out waiting'));
        return;
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

export function gateChipColor(check: GateCheck): 'success' | 'warning' | 'error' {
  if (check === 'pass') return 'success';
  if (check === 'warn') return 'warning';
  return 'error';
}

export function decisionChipColor(decision: InjectGateDecision): 'success' | 'error' | 'warning' {
  if (decision === 'inject') return 'success';
  if (decision === 'reject') return 'error';
  return 'warning';
}

export function resolveCatalogProductName(
  name: string | null | undefined,
  catalogNames: string[]
): string | null {
  const raw = (name || '').trim();
  if (!raw || !catalogNames.length) return null;
  const lowered = raw.toLowerCase();
  const exact = catalogNames.find(n => n.toLowerCase() === lowered);
  if (exact) return exact;
  const hits = catalogNames.filter(
    n => lowered.includes(n.toLowerCase()) || n.toLowerCase().includes(lowered)
  );
  return hits.length === 1 ? hits[0] : null;
}
